import i18n from 'i18next';
import { getLocalToday } from '../../config/timezone';
import type { CalendarDay } from './types';

const DAYS_OF_WEEK_FALLBACK = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];
const DAYS_OF_WEEK_SHORT_FALLBACK = ['L', 'M', 'M', 'G', 'V', 'S', 'D'];
const MONTHS_FALLBACK = [
  'Gennaio',
  'Febbraio',
  'Marzo',
  'Aprile',
  'Maggio',
  'Giugno',
  'Luglio',
  'Agosto',
  'Settembre',
  'Ottobre',
  'Novembre',
  'Dicembre',
];

function getTranslatedArray(key: string, fallback: string[]): string[] {
  const result = i18n.t(key, { returnObjects: true });
  return Array.isArray(result) ? (result as string[]) : fallback;
}

export function getDaysOfWeek(): string[] {
  return getTranslatedArray('calendar.daysOfWeek', DAYS_OF_WEEK_FALLBACK);
}

export function getDaysOfWeekShort(): string[] {
  return getTranslatedArray('calendar.daysOfWeekShort', DAYS_OF_WEEK_SHORT_FALLBACK);
}

export function getMonths(): string[] {
  return getTranslatedArray('calendar.months', MONTHS_FALLBACK);
}

/**
 * `YYYY-MM-DD` from the LOCAL components: never `toISOString()`, which shifts the day in CET/CEST.
 * The year is padded because a DatePicker typed by hand emits `0002-11-03` after the first digit of
 * the year: an unpadded `2-11-03` would not parse back and the field would lose day and month.
 */
export function formatDateKey(date: Date): string {
  const year = String(date.getFullYear()).padStart(4, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

const LEADING_DATE_KEY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})/;

/**
 * Local midnight of the calendar day a `YYYY-MM-DD` key names, `null` when the value is empty, not
 * a key, or a day that does not exist (`2026-02-30`). Only the leading key is read, so a `@db.Date`
 * serialized as `2026-10-05T00:00:00.000Z` gives the 5th. `new Date('2026-10-05')` would parse UTC
 * midnight and show the 4th in negative-offset zones. For a real instant use `getLocalDateIso`.
 */
export function parseDateKey(value: string | null | undefined): Date | null {
  const match = value ? LEADING_DATE_KEY_PATTERN.exec(value) : null;
  if (!match) {
    return null;
  }
  const year = Number(match[1]);
  const monthIndex = Number(match[2]) - 1;
  const day = Number(match[3]);
  // `new Date(year, …)` maps years 0-99 to 1900-1999; `setFullYear` keeps them literal.
  const date = new Date(2000, 0, 1);
  date.setFullYear(year, monthIndex, day);
  const isExistingDay =
    date.getFullYear() === year && date.getMonth() === monthIndex && date.getDate() === day;
  return isExistingDay ? date : null;
}

/** Every day of the month containing `month`, each at local midnight. */
export function getMonthDays(month: Date): Date[] {
  const year = month.getFullYear();
  const monthIndex = month.getMonth();
  const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
  return Array.from(
    { length: daysInMonth },
    (_, dayIndex) => new Date(year, monthIndex, dayIndex + 1),
  );
}

export function getLocalDateKeyFromIso(isoDate: string): string {
  return formatDateKey(parseISODateAsLocal(isoDate));
}

/**
 * Parses an ISO date string to a local-midnight Date object for day-key grouping.
 *
 * - Date-only strings (YYYY-MM-DD): treated as that calendar date in local time.
 * - Datetime strings (contains "T"): converted to the user's local timezone first,
 *   then the local date is extracted. This avoids off-by-one-day errors when a UTC
 *   timestamp like "2026-03-30T22:13:00Z" actually falls on March 31 in UTC+2.
 */
export function parseISODateAsLocal(isoDate: string): Date {
  if (isoDate.includes('T')) {
    const instant = new Date(isoDate);
    return new Date(instant.getFullYear(), instant.getMonth(), instant.getDate());
  }
  return parseDateKey(isoDate) ?? new Date(Number.NaN);
}

/** Monday-start week containing `anchor` (defaults to the current week). */
export function getWeekDays(anchor?: Date): Date[] {
  const base = anchor ?? getLocalToday();
  const dayOfWeek = base.getDay();
  const isoDayOfWeek = dayOfWeek === 0 ? 6 : dayOfWeek - 1;

  const monday = new Date(base);
  monday.setDate(base.getDate() - isoDayOfWeek);
  monday.setHours(0, 0, 0, 0);

  const weekDays: Date[] = [];
  for (let i = 0; i < 7; i++) {
    const day = new Date(monday);
    day.setDate(monday.getDate() + i);
    weekDays.push(day);
  }
  return weekDays;
}

export function getCurrentWeekDays(): Date[] {
  return getWeekDays();
}

export function getCalendarDays(month: Date): CalendarDay[] {
  const year = month.getFullYear();
  const monthIndex = month.getMonth();

  const firstDay = new Date(year, monthIndex, 1);
  const lastDay = new Date(year, monthIndex + 1, 0);

  // Lunedì = 0, Domenica = 6 (ISO week)
  let startDayOfWeek = firstDay.getDay() - 1;
  if (startDayOfWeek < 0) {
    startDayOfWeek = 6;
  }

  const days: CalendarDay[] = [];
  const today = getLocalToday();

  // Giorni del mese precedente
  for (let i = startDayOfWeek - 1; i >= 0; i--) {
    const date = new Date(year, monthIndex, -i);
    days.push({
      date,
      isCurrentMonth: false,
      isToday: date.getTime() === today.getTime(),
    });
  }

  // Giorni del mese corrente
  for (let day = 1; day <= lastDay.getDate(); day++) {
    const date = new Date(year, monthIndex, day);
    days.push({
      date,
      isCurrentMonth: true,
      isToday: date.getTime() === today.getTime(),
    });
  }

  // Giorni del mese successivo per completare la griglia
  const remaining = 42 - days.length; // 6 righe × 7 giorni
  for (let i = 1; i <= remaining; i++) {
    const date = new Date(year, monthIndex + 1, i);
    days.push({
      date,
      isCurrentMonth: false,
      isToday: date.getTime() === today.getTime(),
    });
  }

  return days;
}
